import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckboxExpanderForm, CheckboxExpanderFormItem } from '@mwe/models';
import { everyTrue, focusFirstInvalidInput } from '@mwe/utils';
import { NgClass } from '@angular/common';
import { ClickPreventDefaultDirective } from '../directives/click/click-prevent-default.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { SafeHtmlPipe } from '@mwe/ui/pipes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mwe-checkbox-expander-form',
  templateUrl: './checkbox-expander-form.component.html',
  imports: [NgClass, FormsModule, ReactiveFormsModule, ClickPreventDefaultDirective, TranslatePipe, SafeHtmlPipe],
})
export class CheckboxExpanderFormComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly ref = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  @ViewChildren('collapsible') collapsibles: QueryList<ElementRef>;
  @Input() model: CheckboxExpanderForm;
  @Input() removePadding = false;
  @Output() readonly isValid = new EventEmitter<boolean>();

  form: UntypedFormGroup;
  readonly initialShowMore = new Map<string, boolean>();

  ngAfterViewInit(): void {
    this.ref.detectChanges();
    // this is to undo any change in scroll position by tab-focusing inside while collapsed, see MWE-12458
    [...this.collapsibles].forEach(el => {
      el.nativeElement.addEventListener('focusout', this.onFocusout.bind(this));
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    [...this.collapsibles].forEach(el => el.nativeElement.removeEventListener('focusout', this.onFocusout));
  }

  onFocusout(e): void {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      [...this.collapsibles].forEach(el => (el.nativeElement.scrollTop = 0));
    }
  }

  initForm(): void {
    const formGroup = {};
    this.model.items.forEach(elem => {
      this.initialShowMore.set(elem.name, elem.showMore);
      const formGroupArr = [elem.initialValue] as any;
      if (elem.required === true) {
        formGroupArr.push(Validators.requiredTrue);
      }
      formGroup[elem.name] = formGroupArr;
    });
    this.form = this.formBuilder.group(formGroup);
    this.isValid.emit(this.model.items.every(item => item.initialValue === true));

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isValid.emit(this.form.valid);
    });
  }

  toggleReadMore(itemName: string): void {
    const item = this.model.items.find(elem => elem.name === itemName);
    item.open = !item.open;
    [...this.collapsibles].forEach(el => (el.nativeElement.scrollTop = 0));
  }

  fireValidation(): void {
    Object.keys(this.form.controls).forEach(controlName => this.form.controls[controlName].markAsDirty());
    this.ref.detectChanges();
    const invalidInput = this.elementRef.nativeElement.querySelector('form .is-invalid');
    if (invalidInput) {
      invalidInput.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
    this.isValid.emit(this.form.valid);
    focusFirstInvalidInput(this.injector);
  }

  @HostListener('window:resize', ['$event'])
  checkAllClamps(event: unknown) {
    if (event) {
      this.model.items.forEach(item => {
        if (this.initialShowMore.get(item.name)) {
          const classList = document.getElementById('description-' + item.name).classList;
          let clampAdded = false;
          if (!classList.contains('clamp-2')) {
            // add clamp class to correctly check if text is too long
            classList.add('clamp-2');
            clampAdded = true;
          }
          const isTooLong = this.isLineClampNeeded(item);
          if (item.open && clampAdded) {
            // remove clamp because text is fully shown
            classList.remove('clamp-2');
          }
          item.showMore = isTooLong;
        }
      });
    }
  }

  isLineClampNeeded(item: CheckboxExpanderFormItem): boolean {
    if (this.initialShowMore.get(item.name)) {
      const p = document.getElementById('description-' + item.name);
      const isTooLong = p.scrollHeight > p.clientHeight;
      if (everyTrue(!isTooLong, item.showMore, p.classList.contains('clamp-2'))) {
        // handle situation when page is first time rendered
        item.showMore = false;
      }
      return isTooLong;
    }
    return false;
  }

  isErrorVisible(inputName: string): boolean {
    return this.form?.controls?.[inputName]?.errors && this.form?.controls?.[inputName].dirty;
  }
}
